import { MENU_SECTIONS } from './menu-config';

/*
 * Pure helpers over the menu tree.
 *
 * Kept out of the component so the permission filtering and active-path
 * matching can be reasoned about (and tested) without rendering anything.
 */

export const itemIsVisible = ({ item, menu, hasAction, isSuperAdmin }) => {
  if (menu.superAdminOnly && !isSuperAdmin) return false;
  if (!item.require || item.require.length === 0) return true;
  return item.require.some((action) => hasAction(menu.menu_id, action));
};

const menuHasAnyVisibleItem = ({ menu, hasAction, isSuperAdmin }) => {
  if (menu.superAdminOnly && !isSuperAdmin) return false;
  return menu.items.some((item) =>
    item.children
      ? item.children.some((child) =>
          itemIsVisible({ item: child, menu, hasAction, isSuperAdmin }),
        )
      : itemIsVisible({ item, menu, hasAction, isSuperAdmin }),
  );
};

/**
 * Sections and menus the current admin may see, with empty ones dropped.
 * Filtering happens once here rather than being re-derived at three levels of
 * the render, which is what previously made every menu re-check permissions
 * on every hover.
 */
export const buildVisibleSections = ({ hasAction, isSuperAdmin }) =>
  MENU_SECTIONS.map((section) => ({
    ...section,
    items: section.items.filter((menu) =>
      menuHasAnyVisibleItem({ menu, hasAction, isSuperAdmin }),
    ),
  })).filter((section) => section.items.length > 0);

/** Every routable path in the tree, for longest-prefix active matching. */
export const collectPaths = () => {
  const paths = [];
  MENU_SECTIONS.forEach((section) =>
    section.items.forEach((menu) =>
      menu.items.forEach((item) => {
        if (item.path) paths.push(item.path);
        item.children?.forEach((child) => {
          if (child.path) paths.push(child.path);
        });
      }),
    ),
  );
  return paths;
};

/*
 * The deepest path that prefixes the current location.
 *
 * Longest match wins so that /product and /product/pending don't both light
 * up, and "/" is excluded because it prefixes literally everything.
 */
export const resolveActivePath = (paths, pathname) => {
  let best = null;
  for (const path of paths) {
    if (!path || path === '/') continue;
    if (pathname === path || pathname.startsWith(`${path}/`)) {
      if (!best || path.length > best.length) best = path;
    }
  }
  return best;
};

/** Accordion keys that should start open, given where the user is. */
export const accordionKeysForPath = (activePath) => {
  const keys = {};
  if (!activePath) return keys;
  MENU_SECTIONS.forEach((section) =>
    section.items.forEach((menu) => {
      let openMenu = false;
      menu.items.forEach((item) => {
        if (item.children?.some((child) => child.path === activePath)) {
          keys[`${menu.title}-${item.label}`] = true;
          openMenu = true;
        } else if (item.path === activePath) {
          openMenu = true;
        }
      });
      if (openMenu) keys[`menu-${menu.title}`] = true;
    }),
  );
  return keys;
};

export const menuContainsPath = (menu, activePath) =>
  Boolean(activePath) &&
  menu.items.some((item) =>
    item.children
      ? item.children.some((child) => child.path === activePath)
      : item.path === activePath,
  );

/** A group with exactly one leaf is a link, not an expandable menu. */
export const isSingleLinkMenu = (menu) =>
  menu.items.length === 1 && !menu.items[0].children;
